
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useTimelineInitialization = () => {
  const queryClient = useQueryClient();

  const initializeTimeline = useMutation({
    mutationFn: async (userId: string) => {
      console.log('Initializing timeline for user:', userId);

      // Get the default template timeline ID
      const { data: defaultTemplate, error: templateError } = await supabase
        .from('template_timeline')
        .select('id')
        .eq('is_default', true)
        .single();

      if (templateError || !defaultTemplate) {
        throw new Error('No default template timeline found');
      }

      // Create the user's profile timeline
      const { data: newTimeline, error: timelineError } = await supabase
        .from('profile_timeline')
        .insert({
          user_id: userId,
          template_timeline_id: defaultTemplate.id
        })
        .select()
        .single();

      if (timelineError) {
        throw new Error(`Failed to create timeline: ${timelineError.message}`);
      }

      // Get template sections
      const { data: templateSections, error: sectionsQueryError } = await supabase
        .from('template_timeline_sections')
        .select('*')
        .eq('template_timeline_id', defaultTemplate.id)
        .order('sort_order');

      if (sectionsQueryError) {
        throw new Error(`Failed to get template sections: ${sectionsQueryError.message}`);
      }

      // Create profile timeline sections
      const sectionsToInsert = templateSections.map(section => ({
        profile_timeline_id: newTimeline.id,
        template_timeline_section_id: section.id,
        sort_order: section.sort_order
      }));

      const { error: sectionsError } = await supabase
        .from('profile_timeline_sections')
        .insert(sectionsToInsert);

      if (sectionsError) {
        throw new Error(`Failed to create timeline sections: ${sectionsError.message}`);
      }

      console.log('Timeline initialized successfully for user:', userId);
      return newTimeline;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['profile-for-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['timeline-data'] });
      toast.success('Timeline initialized successfully');
    },
    onError: (error) => {
      console.error('Timeline initialization failed:', error);
      toast.error(`Failed to initialize timeline: ${error.message}`);
    }
  });

  return {
    initializeTimeline: initializeTimeline.mutate,
    isInitializing: initializeTimeline.isPending
  };
};
